import styles from "./objects.module.css";
import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Flex,
  App,
  Spin,
  Button,
  Dropdown,
  Input,
  Form,
  Typography,
  Tag,
  Modal,
  Space,
  Divider,
  notification,
  Tooltip,
  Upload,
} from "antd";
import {
  EditOutlined,
  PrinterOutlined,
  SaveOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
} from "@ant-design/icons";

import SpeakerphoneIcon from "@/icons/SpeakerphoneIcon";
import DuplicateIcon from "@/icons/DuplicateIcon";
import PlusIcon from "@/icons/PlusIcon";
import DotsVerticalIcon from "@/icons/DotsVerticalIcon";
import DownloadIcon from "@/icons/DownloadIcon";

import ObjectFieldsTable from "@/components/tables/ObjectFieldsTable";
import RelatedObjectsTable from "@/components/tables/RelatedObjectsTable";
import RelatedTasksTable from "@/components/tables/RelatedTasksTable";
import RelatedFilesTable from "@/components/tables/RelatedFilesTable";
import TaskExportActions from "@/components/ui/TaskExportActions";
import Breadcrumbs from "@/components/ui/Breadcrumbs";
import EmployeeProfile from "@/components/cards/EmployeeProfile";
import BackBtn from "@/components/ui/BackBtn";
import CustomSelect from "@/components/inputs/CustomSelect";
import ShowDeletedTasksSwitch from "@/components/inputs/ShowDeletedTasksSwitch";
import TaskModal from "@/components/modals/TaskModal";
import FileLinkModal from "@/components/modals/FileLinkModal";
import FileModal from "@/components/modals/FileModal";
import ExportObjectsModal from "@/components/modals/ExportObjectsModal";

import MainContext from "@/layouts/MainLayout/context";
import { UserContext } from "@/context/UserContext";

import { getItem as getDefinition } from "@/api/definitions";
import {
  getObject,
  createObject,
  updateObject,
  deleteObject,
  getPassport,
  getObjectTasks,
  getObjectFiles,
  attachObjectFile,
  detachObjectFile,
} from "@/api/objects";
import { getEmployeeUnitsOptions, getUnitsOptions } from "@/api/units";
import { getSubscribe, subscribe, unsubscribe } from "@/api/notices";
import { getStatusOptions } from "@/api/statuses";
import { getImportanceOptions } from "@/api/importances";
import { getTaskTypeOptions } from "@/api/taskTypes";
import { useNspdSync } from "@/hooks/useNspdSync";

import routes from "@/routes";

import { DEFINITIONS } from "@/constants/definitions";
import { TASK_EXPORT_ENABLED } from "@/constants/tasks";
import { formateISODateToMoment } from "@/utils";

const { Title } = Typography;
const NSPD_CONTEXT = {
  SYNC: "sync",
  MANUAL_SYNC_FIELDS: "manual_sync_fields",
  MANUAL_OTHER_FIELDS: "manual_other_fields",
};

const ObjectPage = ({ isAdmins = false }) => {
  const { message } = App.useApp();
  const { definitionId, objectId, unit_uuid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const copiedObject = location.state?.copiedObject;

  // Состояния для хранения данных
  const [data, setData] = useState({}); // Данные объекта
  const [definition, setDefinition] = useState(); // Описатель объекта
  const [groupsFields, setGroupsFields] = useState([]); // Группы полей
  const [allFields, setAllFields] = useState([]); // Поля объекта
  const [units, setUnits] = useState([]); // список РГ
  const [taskUnits, setTaskUnits] = useState([]); // список РГ для модалки задач
  const [isLoading, setIsLoading] = useState(true);
  const [editable, setEditable] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [passportPending, setPassportPending] = useState(false);
  const [statuses, setStatuses] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [taskTypes, setTaskTypes] = useState([]);
  const [manualNspdContext, setManualNspdContext] = useState(null);

  const [invalidMaskFields, setInvalidMaskFields] = useState({});

  //Связанные сущности
  const [relatedObjects, setRelatedObjects] = useState([]);
  const [relatedTasks, setRelatedTasks] = useState([]);
  const [selectedRelatedTaskUuids, setSelectedRelatedTaskUuids] = useState([]);
  const [showDeletedTasks, setShowDeletedTasks] = useState(false);
  const [relatedTasksPending, setRelatedTasksPending] = useState(false);
  const [relatedFiles, setRelatedFiles] = useState([]);

  const [isDownloadModalShow, setIsDownloadModalShow] = useState(false);

  const [isTaskModalShow, setIsTaskModalShow] = useState(false);
  const [currentTaskItem, setCurrentTaskItem] = useState(null);

  const { resourcesMenu } = useContext(MainContext);

  const [filesPending, setFilesPending] = useState(false);
  const [isModalFileShow, setIsModalFileShow] = useState(false);
  const [isSystemModalShow, setIsSystemModalShow] = useState(false);
  const [currentFile, setCurrentFile] = useState(null);

  const uploadHiddenBtnRef = useRef(null);

  const { user } = useContext(UserContext);
  const [subscribeStatus, setSubscribeStatus] = useState(false);
  const [subscribeLoading, setSubscribeLoading] = useState(false);
  const isDataFetched = useRef(false);
  const isUnitsFetch = useRef(false);
  const stickyHeaderRef = useRef(null);

  useEffect(() => {
    setSelectedRelatedTaskUuids((prev) =>
      prev.filter((uuid) => relatedTasks.some((task) => task.uuid === uuid)),
    );
  }, [relatedTasks]);

  const {
    nspdSync,
    isNspdLoading,
    handleNspdSync,
    resetNspdSync,
    markNspdFieldAsDirty,
  } = useNspdSync({
    data,
    allFields,
    setData,
    setEditable,
    setIsChanged,
    message,
    isAdmins,
  });

  const isUnitRequired = definition?.type !== "repository";

  const hasInvalidMasks = Object.values(invalidMaskFields).some(Boolean);

  const isSaveDisabled =
    !data?.name ||
    !isChanged ||
    hasInvalidMasks ||
    (isUnitRequired && !data.unit_uuid);

  const handleMaskValidate = (fieldName, isValid) => {
    setInvalidMaskFields((prev) => {
      const next = { ...prev };

      if (isValid) {
        delete next[fieldName];
      } else {
        next[fieldName] = true;
      }

      return next;
    });
  };

  const nspdCadastralField = useMemo(() => {
    const selfFields = definition?.fields?.self || [];

    return selfFields.find(
      (field) => field.uuid === definition?.nspd_cadastral_field_uuid,
    );
  }, [definition]);

  const nspdCadastralValue = nspdCadastralField?.field_name
    ? data?.[nspdCadastralField.field_name]
    : null;

  const isNspdSyncDisabled = !nspdCadastralValue;

  const nspdSyncDisabledReason = isNspdSyncDisabled
    ? "Поле кадастрового номера не заполнено"
    : "";

  const breadcrumbItems = useMemo(() => {
    const items = isAdmins
      ? [
          { title: "Административная панель" },

          {
            title: "Все ресурсы",
          },
        ]
      : [
          {
            title: "Главная",
            href: routes.dashboards,
          },

          unit_uuid && data.unit ? { title: data.unit.name } : {},
          {
            title: "Ресурсы",
          },
        ];

    const findPath = (menu, id) => {
      let res = [];

      const search = (items, currentItems) => {
        for (const item of items) {
          if (item.child === id) {
            res = currentItems.concat({ title: item.name });
            return true;
          }

          if (
            item.objects &&
            search(item.objects, currentItems.concat({ title: item.name }))
          ) {
            return true;
          }

          if (
            item.folders &&
            search(item.folders, currentItems.concat({ title: item.name }))
          ) {
            return true;
          }
        }
        return false;
      };

      search(menu, []);

      return res;
    };

    const resourceItems = findPath(resourcesMenu, definitionId);

    const defFolder = resourceItems[resourceItems.length - 1];

    resourceItems[resourceItems.length - 1] = {
      ...defFolder,
      href: unit_uuid
        ? routes.unitObjectList(unit_uuid, definitionId)
        : isAdmins
          ? routes.administration.objectList(definitionId)
          : routes.objectList(definitionId),
    };

    return [...items, ...resourceItems, { title: data?.name || "..." }];
  }, [resourcesMenu, data, definitionId, unit_uuid, isAdmins]);

  // Запрашиваем список РГ
  const fetchUnits = async () => {
    try {
      const res = isAdmins
        ? await getUnitsOptions()
        : await getEmployeeUnitsOptions();

      const units = res.units
        .filter((item) => item?.unit_type?.is_hierarchical)
        .map((item) => ({
          ...item,
          label: item.name,
        }));

      setTaskUnits(
        res.units.map((item) => ({
          ...item,
          label: item.name,
        })),
      );
      setUnits(units);
      isUnitsFetch.current = true;
    } catch (error) {
      console.log(error);
    }
  };

  //Запрашиваем файлы
  const fetchFiles = async () => {
    if (!objectId || objectId === "new") return;

    try {
      setFilesPending(true);

      const files = await getObjectFiles(
        { object_uuid: objectId, definition_uuid: definitionId },
        isAdmins,
      );

      setRelatedFiles(files || []);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setFilesPending(false);
    }
  };

  const fetchRelatedTasks = async (shouldShowDeleted = showDeletedTasks) => {
    if (!objectId || objectId === "new") return;

    setRelatedTasksPending(true);

    try {
      const tasks = await getObjectTasks(
        {
          uuid: objectId,
          definition_uuid: definitionId,
          include_deleted: shouldShowDeleted,
        },
        isAdmins,
      );

      setRelatedTasks(tasks || []);
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setRelatedTasksPending(false);
    }
  };

  const handleShowDeletedTasksChange = (checked) => {
    setShowDeletedTasks(checked);
    setSelectedRelatedTaskUuids([]);
    fetchRelatedTasks(checked);
  };

  const enrichGroupsWithFieldsMeta = (groups = [], fields = []) => {
    const fieldsByUuid = new Map(fields.map((field) => [field.uuid, field]));
    const fieldsByName = new Map(
      fields.map((field) => [field.field_name, field]),
    );

    return groups
      .filter(({ fields }) => !!fields && !!fields.length)
      .sort((a, b) => a.idx - b.idx)
      .map((group) => ({
        ...group,
        fields: group.fields.map((groupField) => {
          const fullField =
            fieldsByUuid.get(groupField.uuid) ||
            fieldsByUuid.get(groupField.field) ||
            fieldsByName.get(groupField.field_name);

          return {
            ...groupField,
            ...fullField,
            ...groupField,
          };
        }),
      }));
  };

  // Функция для получения данных с сервера
  const fetchData = async (defaultUnit) => {
    setIsLoading(true);

    try {
      if (!objectId || objectId === "new") {
        // Если объект новый, получаем определение и устанавливаем поля
        const definition = await getDefinition(definitionId);

        let initialData = {};

        if (copiedObject) {
          initialData = {
            ...copiedObject,
            name: copiedObject.name ? copiedObject.name + " - копия" : "",
          };

          delete initialData.uuid;
          delete initialData.created_at;
          delete initialData.updated_at;

          if (
            units.findIndex(({ uuid }) => uuid === initialData.unit_uuid) === -1
          ) {
            delete initialData.unit_uuid;
            delete initialData.unit;
          }
        }

        if (definition.type !== "repository" && defaultUnit) {
          if (!initialData.unit_uuid) {
            initialData.unit_uuid = defaultUnit;
          }
        }

        setData(initialData);

        setDefinition(definition);
        const { self, template } = definition.fields || {};
        const fields = [...(self || []), ...(template || [])];

        setAllFields(fields);
        setGroupsFields(
          enrichGroupsWithFieldsMeta(definition.groups || [], fields),
        );

        setEditable(true);
        if (copiedObject) {
          setIsChanged(true);
        }
      } else {
        // Если объект существует, получаем его данные и определение
        const [
          { definition, object, referencingObjects },
          tasks,
          files,
          statusesRes,
          prioritiesRes,
          taskTypesRes,
        ] = await Promise.all([
          getObject(definitionId, objectId, isAdmins),
          getObjectTasks(
            {
              uuid: objectId,
              definition_uuid: definitionId,
              include_deleted: showDeletedTasks,
            },
            isAdmins,
          ),
          getObjectFiles(
            { object_uuid: objectId, definition_uuid: definitionId },
            isAdmins,
          ),
          getStatusOptions(),
          getImportanceOptions(),
          getTaskTypeOptions(),
        ]);
        setData(object);
        setDefinition(definition);
        setRelatedObjects(referencingObjects?.data || []);
        setRelatedTasks(tasks || []);
        setRelatedFiles(files || []);
        setStatuses(statusesRes?.map((i) => ({ ...i, label: i.name })));
        setPriorities(prioritiesRes?.map((i) => ({ ...i, label: i.name })));
        setTaskTypes(taskTypesRes?.map((i) => ({ ...i, label: i.name })));
        const { self, template } = definition.fields || {};
        const fields = [...(self || []), ...(template || [])];

        setAllFields(fields);
        setGroupsFields(
          enrichGroupsWithFieldsMeta(definition.groups || [], fields),
        );
        const isObjectSubscription = await getSubscribe({
          user: user.user_uuid,
          uuid: objectId,
        });
        setSubscribeStatus(isObjectSubscription.is_subscribed);

        // Из-за того что раньше проверки на иерархию не было, в базе есть ресурсы с неподходящими теперь РГ, и что бы не вываливался uuid в селекте, добавим текущую РГ в список
        const hasUnit = units.some((u) => u.uuid === object.unit_uuid);

        if (!hasUnit && object.unit)
          setUnits((prevUnits) => [
            ...prevUnits,
            {
              ...object.unit,
              label: object.unit.name,
            },
          ]);
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Первоначальный запрос данных при загрузке компонента
  useEffect(() => {
    isDataFetched.current = false;
    isUnitsFetch.current = false;

    fetchUnits();
    // eslint-disable-next-line
  }, [definitionId, objectId]);

  // Срабатываем fetchData после обновления units
  useEffect(() => {
    if (!isDataFetched.current && isUnitsFetch.current) {
      isDataFetched.current = true;
      setEditable(false);
      fetchData(unit_uuid || null);
    }
    // eslint-disable-next-line
  }, [units, isDataFetched, isUnitsFetch]);

  // Скролл до нужного раздела страницы
  const scrollTo = (elementId) => {
    // Ищем целевой DOM-элемент по id раздела
    const targetElement = document.getElementById(elementId);
    if (!targetElement) return;

    // Находим ближайший контейнер, который реально прокручивается по Y
    const getScrollContainer = (element) => {
      let parent = element.parentElement;

      while (parent) {
        const style = window.getComputedStyle(parent);
        const overflowY = style.overflowY;
        const isScrollable =
          (overflowY === "auto" || overflowY === "scroll") &&
          parent.scrollHeight > parent.clientHeight;

        if (isScrollable) return parent;
        parent = parent.parentElement;
      }

      return document.scrollingElement || document.documentElement;
    };

    // Высота закрепленной шапки, чтобы заголовок раздела не оказался под ней
    const headerHeight = stickyHeaderRef.current?.offsetHeight || 0;
    const scrollContainer = getScrollContainer(targetElement);
    const targetRect = targetElement.getBoundingClientRect();
    const isPageScrollContainer =
      scrollContainer === document.scrollingElement ||
      scrollContainer === document.documentElement;

    // Для страницы и вложенного скролл-контейнера расчет позиции немного разный
    const targetPosition = isPageScrollContainer
      ? targetRect.top + window.pageYOffset - headerHeight
      : targetRect.top -
        scrollContainer.getBoundingClientRect().top +
        scrollContainer.scrollTop -
        headerHeight;

    // Плавный скролл к секции; защита от отрицательных значений
    scrollContainer.scrollTo({
      top: Math.max(targetPosition, 0),
      behavior: "smooth",
    });
  };

  // Получение паспорта объекта
  const handleGetPassport = async () => {
    setPassportPending(true);

    try {
      const res = await getPassport(definitionId, objectId);

      if (res?.uuid) {
        const passportUrl = `${window.location.origin}/passport/${res.uuid}`;
        window.open(passportUrl, "_blank");
      } else {
        message.open({
          type: "error",
          content: "UUID паспорта не найден",
          duration: 10,
        });
      }
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setPassportPending(false);
    }
  };

  const isNspdFieldName = (fieldName) => {
    return allFields.some(
      (field) =>
        field.field_name === fieldName && field.nspd_key_uuid && field.nspd_key,
    );
  };

  // Обработчик изменения данных объекта
  const handleObjectChange = (field, value, source = "user") => {
    if (source === "user") {
      markNspdFieldAsDirty(field);

      if (definition?.is_nspd_enabled) {
        setManualNspdContext((prev) => {
          if (isNspdFieldName(field)) {
            return NSPD_CONTEXT.MANUAL_SYNC_FIELDS;
          }

          return prev || NSPD_CONTEXT.MANUAL_OTHER_FIELDS;
        });
      }
    }

    if (field === "unit_uuid") {
      setData((prevData) => {
        const newData = { ...prevData, unit_uuid: value };

        // типы полей, значения которых необходимо сбросить, т.к. выбрали новую РГ
        const resettableTypes = new Set(["file", ...DEFINITIONS]);

        allFields.forEach((f) => {
          if (resettableTypes.has(f.type)) {
            newData[f.field_name] = null;
          }
        });

        return newData;
      });
    } else {
      setData((prevData) => ({
        ...prevData,
        [field]: value,
      }));
    }

    setIsChanged(true);
  };

  // Функция для форматирования данных  перед отправкой на сервер
  const formateDate = (data) => {
    const newData = { ...data };

    Object.keys(newData).forEach((key) => {
      const value = newData[key];

      if (value && typeof value === "object" && "uuid" in value) {
        newData[key] = value.uuid;
      }
      if (value && typeof value === "object" && "rows" in value) {
        delete newData[key];
      }
    });

    return newData;
  };

  // Обработчик сохранения данных объекта
  const handleSave = async () => {
    setIsLoading(true);

    const getNspdContext = () => {
      if (!definition?.is_nspd_enabled) {
        return null;
      }

      if (nspdSync.isSuccessful && !nspdSync.isDirtyByNspdField) {
        return NSPD_CONTEXT.SYNC;
      }

      return manualNspdContext || NSPD_CONTEXT.MANUAL_OTHER_FIELDS;
    };

    const nspdContext = getNspdContext();

    const updatedData = {
      ...formateDate(data),
      ...(definition?.is_nspd_enabled ? { nspdContext } : {}),
    };

    try {
      let res;
      if (objectId === "new") {
        // Создание нового объекта
        res = await createObject({
          definition_uuid: definitionId,
          ...updatedData,
        });
        // Fixme: убрать если бекенд начнет возвращать объекты
        fetchData(unit_uuid ? unit_uuid : units[0]?.uuid);
        message.open({
          type: "success",
          content: "Объект успешно создан",
          duration: 2,
        });
        navigate(
          unit_uuid
            ? routes.unitObjectDetail(unit_uuid, definitionId, res.uuid)
            : isAdmins
              ? routes.administration.objectDetail(definitionId, res.uuid)
              : routes.objectDetail(definitionId, res.uuid),
          { replace: true },
        );
      } else {
        // Обновление существующего объекта
        res = await updateObject({
          definition_uuid: definitionId,
          ...updatedData,
        });
        // Fixme: убрать если бекенд начнет возвращать объекты
        fetchData(unit_uuid ? unit_uuid : units[0]?.uuid);
        message.open({
          type: "success",
          content: "Изменения сохранены",
          duration: 2,
        });
      }
      setIsChanged(false);

      return res;
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      resetNspdSync();
    }
  };

  const handleSaveAndClose = async () => {
    const res = await handleSave();

    if (res) {
      // setEditable(false);
      navigate(-1);
    }
  };

  const handleBack = (e) => {
    e.preventDefault();

    if (editable && !isChanged && objectId !== "new") {
      setEditable(false);
    } else {
      navigate(-1);
    }
  };

  const showDelBtn =
    (objectId !== "new" &&
      !data.readOnly &&
      definition?.type !== "repository") ||
    isAdmins;

  const canEditAndDuplicate =
    !data.readOnly && (definition?.type !== "repository" || isAdmins);

  const handleDuplicate = () => {
    navigate(
      unit_uuid
        ? routes.unitObjectNew(unit_uuid, definitionId)
        : isAdmins
          ? routes.administration.objectNew(definitionId)
          : routes.objectNew(definitionId),
      {
        state: { copiedObject: data },
      },
    );
  };

  // Удаление объекта
  const handleDelete = () => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить ${data.name}?`,
      content: "Это действие нельзя будет отменить. Данные будут удалены.",
      onOk() {
        handleConfirmDelete();
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем объект после подтверждения
  const handleConfirmDelete = async () => {
    try {
      await deleteObject({ uuid: data.uuid, definition_uuid: definitionId });

      navigate(
        isAdmins
          ? routes.administration.objectList(definitionId)
          : routes.objectList(definitionId),
      );
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    }
  };

  // Подписка
  const handleSubscribe = async () => {
    try {
      const item = {
        type: "object",
        definition_uuid: definitionId,
        items: [objectId],
        user: user.user_uuid,
      };

      if (subscribeStatus) {
        await unsubscribe(item);
      } else {
        await subscribe(item);
      }

      fetchData(unit_uuid ? unit_uuid : units[0]?.uuid);
      showNotice(!subscribeStatus);
    } catch (error) {
      message.open({ type: "error", content: error.message, duration: 10 });
    }
    setSubscribeLoading(false);
  };

  const showNotice = (isSubscribed) => {
    const style = isSubscribed
      ? {
          border: "1px solid #B7EB8F",
          background: "#f6ffed",
          borderRadius: "8px",
          width: "500px",
        }
      : {
          border: "1px solid #91CAFF",
          background: "#e6f4ff",
          borderRadius: "8px",
          width: "500px",
        };

    const taskLabel = `${data.name}`;

    notification.open({
      message: isSubscribed
        ? `Подписка добавлена: ${taskLabel}`
        : `Подписка отменена: ${taskLabel}`,
      description: isSubscribed
        ? "Вы подписались на объект раздела. Все события, связанные с этим объектом, будут у вас в уведомлениях."
        : "Вы отписались от уведомлений на объект раздела.",
      icon: null,
      duration: 4,
      pauseOnHover: true,
      placement: "bottomRight",
      style,
    });
  };

  //Экспорт объекта
  const exportFields = useMemo(() => {
    if (!definition || !definition.fields) return [];
    return [
      ...(definition.fields?.self?.filter((field) => field?.uuid) || []),
      ...(definition.fields?.template?.filter((field) => field?.uuid) || []),
    ];
  }, [definition]);

  const handleExport = ({ folder_uuid, uuid, original_name }) => {
    setIsDownloadModalShow(false);

    // if (folder_uuid) navigate(routes.folder(folder_uuid));

    if (uuid) {
      const link = document.createElement("a");
      link.href = `/api/file/download?uuid=${uuid}`;
      link.download = original_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notification.success({
        message: "Экспорт успешно завершен",
        duration: 10,
        showProgress: true,
        pauseOnHover: true,
        style: {
          width: 480,
        },
        description: (
          <>
            {folder_uuid && (
              <p>
                Файл сохранен в личную рабочую группу в папку{" "}
                <a href={routes.folder(folder_uuid)}>Выгрузка</a>.
              </p>
            )}
          </>
        ),
      });
    }
  };

  // Обработчик добавления связанной задачи. Открывает модальное окно
  const handleAddNewTask = () => {
    const unitUuidFromData = data?.unit_uuid;

    const resolvedUnitUuid = taskUnits.some(
      (unit) => unit.uuid === unitUuidFromData,
    )
      ? unitUuidFromData
      : taskUnits[0]?.uuid;

    setCurrentTaskItem({
      unit_uuid: resolvedUnitUuid,
      resources: [{ object_uuid: data?.uuid, definition_uuid: definitionId }],
    });

    setIsTaskModalShow(true);
  };

  // Обработчик сохранения связанной задачи. Отправляет запрос на сервер и обновляет данные
  const handleSaveTask = () => {
    setIsTaskModalShow(false);
    fetchData();
  };

  const actionMenuItems = [
    objectId !== "new" && objectId
      ? {
          key: "subscribe",
          label: subscribeStatus
            ? "Отписаться от обновлений"
            : "Подписаться на обновления",
          disabled: subscribeLoading,
          onClick: handleSubscribe,
        }
      : null,
    {
      key: "divider-1",
      type: "divider",
    },
    canEditAndDuplicate
      ? {
          key: "edit",
          label: "Редактировать",
          onClick: () => setEditable(true),
        }
      : null,
    canEditAndDuplicate
      ? {
          key: "duplicate",
          label: "Дублировать",
          onClick: handleDuplicate,
        }
      : null,
    {
      key: "export",
      label: "Скачать объект",
      onClick: () => setIsDownloadModalShow(true),
    },
    // {
    //   key: "print",
    //   label: "Печать",
    //   onClick: () => window.print(),
    // },
    {
      key: "divider-2",
      type: "divider",
    },
    definition?.type === "object"
      ? {
          key: "passport",
          label: "Паспорт объекта",
          disabled: passportPending,
          onClick: handleGetPassport,
        }
      : null,
  ].filter(Boolean);

  const sectionLinks = [
    ...groupsFields.map((group) => ({
      key: group.uuid,
      label: group.name,
      target: group.uuid,
    })),
    ...(!editable && relatedObjects.length
      ? [
          {
            key: "related-objects",
            label: "Связанные объекты",
            target: "related-objects",
          },
        ]
      : []),
    ...(!editable && definition?.type === "object"
      ? [
          {
            key: "related-tasks",
            label: "Связанные задачи",
            target: "related-tasks",
          },
        ]
      : []),
    ...(!editable
      ? [
          {
            key: "related-files",
            label: "Связанные файлы",
            target: "related-files",
          },
        ]
      : []),
  ];

  const addFileMenu = {
    items: [
      { key: "device", label: "Добавить файл с устройства" },
      { key: "system", label: "Добавить файл из системы" },
    ],
    onClick: ({ key }) => {
      if (key === "device") {
        uploadHiddenBtnRef.current?.click();
      } else if (key === "system") {
        setIsSystemModalShow(true);
      }
    },
  };

  const uploadsProps = {
    name: "file",
    action: "/api/file/upload",
    multiple: false,
    showUploadList: false,
  };

  const handleDropFile = ({ file }) => {
    const { status } = file;

    if (status === "uploading") {
      setFilesPending(true);
      return;
    }

    if (status === "done") {
      setFilesPending(false);

      const fileObj = file.response.files[0];
      const name = fileObj.original_name.split(".").slice(0, -1).join(".");

      setCurrentFile({
        ...fileObj,
        name,
        unit_uuid: data.unit_uuid,
        object_uuid: objectId,
        definition_uuid: definitionId,
        isSystem: false,
      });

      setIsModalFileShow(true);
    } else if (status === "error") {
      message.error(
        `Загрузка файла ${file.name} не удалась. Попробуйте еще раз.`,
      );
      setFilesPending(false);
    }
  };

  const getFileUuid = (file) => {
    if (!file) return null;
    if (typeof file === "string") return file;

    return file.uuid || file.file_uuid || file.file?.uuid || null;
  };

  const getAttachedFileUuid = (file) => {
    return file?.file_uuid || file?.uuid || file?.file?.uuid || null;
  };

  const isFileAlreadyAttached = (fileUuid) => {
    return relatedFiles.some((file) => getAttachedFileUuid(file) === fileUuid);
  };

  const attachFileToObject = async (file) => {
    const fileUuid = getFileUuid(file);

    if (!fileUuid) {
      message.error("UUID файла не найден");
      return false;
    }

    if (isFileAlreadyAttached(fileUuid)) {
      message.warning("Этот файл уже прикреплен к объекту");
      return false;
    }

    try {
      setFilesPending(true);

      await attachObjectFile({
        file_uuid: fileUuid,
        object_uuid: objectId,
        definition_uuid: definitionId,
      });

      await fetchFiles();

      return true;
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });

      setFilesPending(false);
      return false;
    }
  };

  const handleSaveFileLink = async (payload) => {
    const attached = await attachFileToObject(payload);

    if (attached) {
      setIsSystemModalShow(false);
    }
  };

  const handleSaveFile = async (savedFile) => {
    const attached = await attachFileToObject(savedFile || currentFile);

    if (attached) {
      setIsModalFileShow(false);
      setCurrentFile(null);
    }
  };

  const handleDetachFile = (file) => {
    Modal.confirm({
      title: `Открепить файл ${file.name}?`,
      content:
        "Файл будет откреплен от объекта, но не будет удален из общего списка файлов.",
      okText: "Открепить",
      cancelText: "Отмена",
      onOk: async () => {
        try {
          setFilesPending(true);
          await detachObjectFile(
            {
              uuid: file.link_uuid,
            },
            isAdmins,
          );

          await fetchFiles();
        } catch (error) {
          message.open({
            type: "error",
            content: error.message,
            duration: 10,
          });
          setFilesPending(false);
        }
      },
    });
  };

  const isExistingObject = objectId !== "new";
  const shouldShowResourceDates =
    isExistingObject && (data?.created_at || data?.updated_at);

  return (
    <>
      {isLoading ? (
        <div className="spin">
          <Spin size="large" />
        </div>
      ) : (
        definition && (
          <Flex vertical gap={30} id="top">
            <Flex vertical gap={10}>
              {/* Закрепленная шапка */}
              <Flex
                vertical
                gap={10}
                className={styles.object_page__header}
                ref={stickyHeaderRef}
              >
                <Breadcrumbs items={breadcrumbItems} />

                <Flex
                  gap={20}
                  align="flex-start"
                  justify="space-between"
                  className={[
                    styles.object_page__title_section,
                    editable ? styles.editable : "",
                  ]}
                >
                  <Flex gap={10} align="flex-start" wrap>
                    <BackBtn onClick={handleBack}>{data.name}</BackBtn>
                    {data.unit && <Tag color="blue">{data.unit?.name}</Tag>}
                  </Flex>

                  {/* Кнопки действий */}
                  {editable ? (
                    <Flex gap={10} style={{ marginLeft: "auto" }}>
                      <Button
                        size="large"
                        icon={<SaveOutlined />}
                        disabled={isSaveDisabled}
                        onClick={handleSave}
                      >
                        Сохранить
                      </Button>
                      <Button
                        type="primary"
                        size="large"
                        icon={<SaveOutlined />}
                        disabled={isSaveDisabled}
                        onClick={handleSaveAndClose}
                      >
                        Сохранить и закрыть
                      </Button>
                    </Flex>
                  ) : (
                    <>
                      <Flex
                        gap={10}
                        className={styles.object_page__actionsDesktop}
                      >
                        {objectId !== "new" && objectId && (
                          <Button
                            title={
                              subscribeStatus
                                ? "Отписаться от обновлений"
                                : "Подписаться на обновления"
                            }
                            type={subscribeStatus ? "primary" : ""}
                            icon={<SpeakerphoneIcon />}
                            onClick={handleSubscribe}
                            size="large"
                            disabled={subscribeLoading}
                          />
                        )}
                        <Button
                          size="large"
                          icon={<PrinterOutlined />}
                          onClick={() => window.print()}
                        />
                        {!data.readOnly &&
                          (definition.type !== "repository" || isAdmins) && (
                            <>
                              <Button
                                size="large"
                                icon={<DownloadIcon />}
                                onClick={() => setIsDownloadModalShow(true)}
                              />

                              <Button
                                // type="primary"
                                size="large"
                                icon={<EditOutlined />}
                                onClick={() => setEditable(true)}
                              />

                              <Button
                                size="large"
                                icon={<DuplicateIcon />}
                                onClick={handleDuplicate}
                              />
                            </>
                          )}

                        {definition.type === "object" && (
                          <Button
                            type="primary"
                            size="large"
                            onClick={() => handleGetPassport()}
                            loading={passportPending}
                          >
                            Паспорт объекта
                          </Button>
                        )}
                      </Flex>

                      <div className={styles.object_page__actionsMobile}>
                        <Dropdown
                          menu={{ items: actionMenuItems }}
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <Button
                            color="default"
                            variant="text"
                            type="text"
                            icon={<DotsVerticalIcon />}
                          />
                        </Dropdown>
                      </div>
                    </>
                  )}
                </Flex>

                {(data?.author || shouldShowResourceDates) && (
                  <Flex vertical gap={6}>
                    {data?.author && (
                      <Flex gap={12} wrap align="center">
                        <Typography.Text type="secondary">
                          Автор:
                        </Typography.Text>
                        <EmployeeProfile
                          {...data.author}
                          fullName={false}
                          avatarSize={24}
                          tooltip
                        />
                      </Flex>
                    )}

                    {isExistingObject && data?.created_at && (
                      <Flex gap={12} wrap align="center">
                        <Typography.Text type="secondary">
                          Дата создания:
                        </Typography.Text>
                        <Typography.Text>
                          {formateISODateToMoment(data.created_at)}
                        </Typography.Text>
                      </Flex>
                    )}

                    {isExistingObject && data?.updated_at && (
                      <Flex gap={12} wrap align="center">
                        <Typography.Text type="secondary">
                          Дата изменения:
                        </Typography.Text>
                        <Typography.Text>
                          {formateISODateToMoment(data.updated_at)}
                        </Typography.Text>
                      </Flex>
                    )}
                  </Flex>
                )}

                {/* Навигация по разделам */}
                {/* Десктоп */}
                <Space
                  className={`not-print ${styles.object_page__sectionsNav}`}
                  size={0}
                  separator={
                    <Divider
                      orientation="vertical"
                      style={{ borderColor: "var(--color-gray)" }}
                    />
                  }
                >
                  {sectionLinks.map((link) => (
                    <a
                      key={link.key}
                      href={`#${link.target}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollTo(link.target);
                      }}
                    >
                      {link.label}
                    </a>
                  ))}
                </Space>

                {/* Мобилка */}
                <div
                  className={`not-print w-100 ${styles.object_page__sectionsNavMobile}`}
                >
                  <Dropdown
                    trigger={["click"]}
                    placement="bottomLeft"
                    menu={{
                      items: sectionLinks.map((link) => ({
                        key: `mobile-${link.key}`,
                        label: link.label,
                      })),
                      onClick: ({ key }) => {
                        const selectedLink = sectionLinks.find(
                          (link) => `mobile-${link.key}` === key,
                        );

                        if (selectedLink) {
                          scrollTo(selectedLink.target);
                        }
                      },
                    }}
                  >
                    <Button color="default" variant="filled">
                      Разделы
                    </Button>
                  </Dropdown>
                </div>
              </Flex>

              {/* Описатель и его описание */}
              {definition?.type === "object" && definition?.description && (
                <div>
                  <Title level={4}>{definition.name}</Title>
                  <div>{definition.description}</div>
                </div>
              )}

              {definition.is_nspd_enabled && editable && (
                <Tooltip title={nspdSyncDisabledReason}>
                  <span
                    style={{
                      maxWidth: 300,
                      marginTop: 20,
                      display: "inline-block",
                    }}
                  >
                    <Button
                      size="large"
                      loading={isNspdLoading}
                      onClick={() => handleNspdSync(nspdCadastralValue)}
                      disabled={isNspdSyncDisabled}
                      style={{ width: "100%" }}
                    >
                      Синхронизировать данные с НСПД
                    </Button>
                  </span>
                </Tooltip>
              )}

              {data?.nspd_sync?.last_successful_sync_at && (
                <Flex vertical gap={5}>
                  <Flex gap={12} wrap>
                    <Typography.Text type="secondary">
                      Дата последней синхронизации c НСПД:
                    </Typography.Text>
                    <Typography.Text type="secondary">
                      {new Date(
                        data.nspd_sync.last_successful_sync_at,
                      ).toLocaleString("ru-RU")}
                    </Typography.Text>
                  </Flex>

                  {data?.nspd_sync?.initiated_by_employee && (
                    <Flex gap={12} wrap>
                      <Typography.Text type="secondary">
                        Автор синхронизации:
                      </Typography.Text>
                      <EmployeeProfile
                        {...data.nspd_sync.initiated_by_employee}
                        fullName={false}
                        avatarSize={24}
                        tooltip
                      />
                    </Flex>
                  )}
                </Flex>
              )}

              {/* Инпут для названия и выбор РГ */}
              {editable && (
                <Flex gap={20} style={{ marginTop: 10, marginBottom: "-10px" }}>
                  <Form.Item
                    label="Название"
                    layout="vertical"
                    style={{ flex: 1 }}
                  >
                    <Input
                      autoFocus={objectId === "new"}
                      // size="large"
                      placeholder="Введите название"
                      value={data.name}
                      onChange={(e) =>
                        handleObjectChange("name", e.target.value)
                      }
                    />
                  </Form.Item>

                  {definition?.type !== "repository" && (
                    <Form.Item
                      label="Рабочая группа"
                      layout="vertical"
                      required
                      validateStatus={
                        !data.unit_uuid && isChanged ? "error" : ""
                      }
                      help={
                        !data.unit_uuid && isChanged
                          ? "Обязательное поле"
                          : null
                      }
                    >
                      <CustomSelect
                        style={{ width: 200 }}
                        value={data.unit_uuid}
                        showSearch
                        filterOption={false}
                        options={units}
                        disabled={objectId !== "new"}
                        fieldNames={{ value: "uuid" }}
                        onChange={(newValue) => {
                          handleObjectChange("unit_uuid", newValue || null);
                        }}
                      />
                    </Form.Item>
                  )}
                </Flex>
              )}

              {/* Разделы объекта */}
              {groupsFields.map((group) => {
                return (
                  <div key={group.uuid} id={group.uuid}>
                    <Title level={4} style={{ margin: "20px 0 5px" }}>
                      {group.name}
                    </Title>
                    <ObjectFieldsTable
                      editable={editable}
                      fields={group.fields || []}
                      object={data}
                      units={units}
                      unit_uuid={unit_uuid}
                      isAdmins={isAdmins}
                      onFieldChange={(fieldName, val) =>
                        handleObjectChange(fieldName, val, "user")
                      }
                      color={definition.color}
                      onRefresh={() =>
                        fetchData(unit_uuid ? unit_uuid : units[0]?.uuid)
                      }
                      onRelatedTasksRefresh={fetchRelatedTasks}
                      definition_uuid={definition.uuid}
                      onMaskValidate={handleMaskValidate}
                      nspdChangedFields={nspdSync.appliedFields.map(
                        (item) => item.field_name,
                      )}
                      nspdFailedFields={nspdSync.failedFields}
                      nspdCadastralFieldUuid={
                        definition.nspd_cadastral_field_uuid
                      }
                    />
                  </div>
                );
              })}

              {/* Связанные объекты */}
              {!!relatedObjects.length && !editable && (
                <div id="related-objects">
                  <Title level={4} style={{ margin: "20px 0 5px" }}>
                    Связанные объекты
                  </Title>
                  <RelatedObjectsTable
                    data={relatedObjects}
                    unit_uuid={unit_uuid}
                    isAdmins={isAdmins}
                  />
                </div>
              )}

              {/* Связанные задачи */}
              {!editable && definition.type === "object" && (
                <div id="related-tasks">
                  <Flex justify="space-between">
                    <Title level={4} style={{ margin: "20px 0 5px" }}>
                      Связанные задачи
                    </Title>
                    <Flex align="center" gap={8} style={{ margin: "auto 0 5px" }}>
                      <TaskExportActions
                        selectedTasks={selectedRelatedTaskUuids}
                        onClear={() => setSelectedRelatedTaskUuids([])}
                        isAdmins={isAdmins}
                      />
                      {(definition.use_permission_rules ||
                        !data.readOnly ||
                        isAdmins) && (
                        <Button
                          type="primary"
                          icon={<PlusIcon />}
                          onClick={handleAddNewTask}
                        />
                      )}
                    </Flex>
                  </Flex>

                  <ShowDeletedTasksSwitch
                    checked={showDeletedTasks}
                    onChange={handleShowDeletedTasksChange}
                    disabled={relatedTasksPending}
                    style={{ marginBlock: 12 }}
                  />

                  <RelatedTasksTable
                    data={relatedTasks || []}
                    unit_uuid={unit_uuid}
                    isAdmins={isAdmins}
                    statuses={statuses}
                    priorities={priorities}
                    selectedRowKeys={
                      TASK_EXPORT_ENABLED ? selectedRelatedTaskUuids : undefined
                    }
                    onRowsSelect={
                      TASK_EXPORT_ENABLED
                        ? setSelectedRelatedTaskUuids
                        : undefined
                    }
                  />
                </div>
              )}

              {/* Связанные файлы */}
              {!editable && (
                <div id="related-files">
                  <Flex justify="space-between">
                    <Title level={4} style={{ margin: "20px 0 5px" }}>
                      Связанные файлы
                    </Title>
                    {(!data.readOnly || isAdmins) && (
                      <Dropdown
                        menu={addFileMenu}
                        placement="bottomRight"
                        trigger={["click"]}
                      >
                        <Tooltip placement="bottomRight" title="Добавить файл">
                          <Button
                            type="primary"
                            icon={<PlusIcon />}
                            style={{ margin: "auto 0 5px" }}
                          />
                        </Tooltip>
                      </Dropdown>
                    )}
                  </Flex>
                  <RelatedFilesTable
                    data={relatedFiles || []}
                    loading={filesPending}
                    show_description
                    readOnly={data.readOnly && !isAdmins}
                    onDeleteItem={handleDetachFile}
                  />
                </div>
              )}
            </Flex>

            <Button
              icon={<ArrowUpOutlined />}
              style={{ alignSelf: "flex-start" }}
              onClick={(e) => {
                e.preventDefault();
                scrollTo("top");
              }}
            >
              Наверх
            </Button>

            {!!showDelBtn && (
              <Button
                danger
                type="link"
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                className="no-padding"
                style={{ alignSelf: "flex-start" }}
              >
                Удалить
              </Button>
            )}
          </Flex>
        )
      )}

      {isTaskModalShow && (
        <TaskModal
          isAdmins={isAdmins}
          data={currentTaskItem}
          statuses={statuses}
          priorities={priorities}
          taskTypes={taskTypes}
          units={taskUnits}
          visible={isTaskModalShow}
          onSave={handleSaveTask}
          onCancel={() => setIsTaskModalShow(false)}
          unit_uuid={currentTaskItem?.unit_uuid || null}
          canEditUnit={
            definition.type === "object"
              ? definition.use_permission_rules
              : true
          }
        />
      )}

      {isDownloadModalShow && (
        <ExportObjectsModal
          objects={[data]}
          isSingleObject
          fields={exportFields}
          definition={definitionId}
          visible={isDownloadModalShow}
          onSave={handleExport}
          onCancel={() => setIsDownloadModalShow(false)}
          onClearAndCancel={() => {
            setIsDownloadModalShow(false);
          }}
          isAdmins={isAdmins}
        />
      )}

      {isModalFileShow && (
        <FileModal
          data={currentFile}
          visible={isModalFileShow}
          units={units}
          onSave={handleSaveFile}
          onCancel={() => setIsModalFileShow(false)}
        />
      )}

      {isSystemModalShow && (
        <FileLinkModal
          visible={isSystemModalShow}
          onSave={handleSaveFileLink}
          onCancel={() => setIsSystemModalShow(false)}
          isAdmins={isAdmins}
        />
      )}

      <Upload
        {...uploadsProps}
        onChange={handleDropFile}
        style={{ display: "none" }}
      >
        <Button ref={uploadHiddenBtnRef} style={{ display: "none" }} />
      </Upload>
    </>
  );
};

export default ObjectPage;
